/**
 * RBAC Permission Utilities
 * Centralized role-based access control logic for the application
 *
 * Role Hierarchy:
 * USER (1) < OPERATOR (2) < ADMIN (3) < SUPERADMIN (4)
 *
 * Permissions:
 * - USER: Bookings only
 * - OPERATOR: USER + Approvals
 * - ADMIN: OPERATOR + Rooms + Statistics
 * - SUPERADMIN: Everything
 */

// Type definition for user roles
export type UserRole = 'USER' | 'OPERATOR' | 'ADMIN' | 'SUPERADMIN';

// Role hierarchy mapping (higher number = more permissions)
const ROLE_HIERARCHY: Record<UserRole, number> = {
  'USER': 1,
  'OPERATOR': 2,
  'ADMIN': 3,
  'SUPERADMIN': 4
};

/**
 * Check if user has minimum required role
 * @param userRole Current user's role
 * @param requiredRole Minimum role required
 * @returns true if user meets or exceeds the required role level
 */
export function hasMinimumRole(userRole: UserRole, requiredRole: UserRole): boolean {
  return ROLE_HIERARCHY[userRole] >= ROLE_HIERARCHY[requiredRole];
}

// Admin tab identifiers
export type AdminTab =
  | 'approvals'
  | 'users'
  | 'rooms'
  | 'equipment'
  | 'staff'
  | 'expertises'
  | 'activity'
  | 'calendar'
  | 'appearance'
  | 'plugins'
  | 'statistics'
  | 'recurring'
  | 'operating-hours'
  | 'booking-limits';

/**
 * Admin Tab Permission Configuration
 * Maps each admin tab to the minimum role required to access it
 */
export const ADMIN_TAB_PERMISSIONS: Record<AdminTab, UserRole> = {
  'approvals': 'OPERATOR',      // OPERATOR+ can see approvals
  'equipment': 'OPERATOR',      // OPERATOR+ can manage equipment
  'rooms': 'ADMIN',             // ADMIN+ can manage rooms
  'staff': 'ADMIN',             // ADMIN+ can manage staff
  'expertises': 'ADMIN',        // ADMIN+ can manage expertises
  'calendar': 'ADMIN',          // ADMIN+ can view Google Calendar status
  'statistics': 'ADMIN',        // ADMIN+ can view stats
  'recurring': 'ADMIN',          // ADMIN+ can manage recurring rules
  'operating-hours': 'ADMIN',   // ADMIN+ can manage operating hours
  'booking-limits': 'ADMIN',   // ADMIN+ can manage booking limits
  'plugins': 'SUPERADMIN',      // SUPERADMIN only can manage plugins
  'users': 'SUPERADMIN',        // SUPERADMIN only can manage users
  'activity': 'SUPERADMIN',     // SUPERADMIN only can view audit logs
  'appearance': 'SUPERADMIN'    // SUPERADMIN only can manage appearance
};

/**
 * Check if user can access a specific admin tab
 * @param userRole Current user's role
 * @param tabId Admin tab identifier
 * @returns true if user has permission to access the tab
 */
export function canAccessAdminTab(userRole: UserRole, tabId: AdminTab): boolean {
  const requiredRole = ADMIN_TAB_PERMISSIONS[tabId];
  return hasMinimumRole(userRole, requiredRole);
}

/**
 * Check if user can access the admin section at all
 * @param userRole Current user's role
 * @returns true if user can access admin section (OPERATOR+)
 */
export function canAccessAdminSection(userRole: UserRole): boolean {
  return hasMinimumRole(userRole, 'OPERATOR');
}

/**
 * Get all admin tabs that the user can access
 * @param userRole Current user's role
 * @returns Array of admin tab IDs the user can access
 */
export function getAccessibleAdminTabs(userRole: UserRole): AdminTab[] {
  return (Object.keys(ADMIN_TAB_PERMISSIONS) as AdminTab[]).filter(
    tabId => canAccessAdminTab(userRole, tabId)
  );
}
